'use client';
import { useState, FC, useMemo } from 'react';

import SyncLoader from 'react-spinners/SyncLoader';
import { useTheme } from 'styled-components';
import { useParams } from 'next/navigation';
import dynamic from 'next/dynamic';

import { HotelViewModel } from '@api/index';
import { useFiltersContext } from '@contexts/Filters';
import HotelCardSkeleton from '@components/views/hotel/listing/components/ListingSkeleton/HotelCardSkeleton';
import NotFound from '@components/Errors/404/404';
import {
  ItemsRow,
  ItemsRowWrapper,
  ListingItemContainer,
  LoadMoreButton,
  LoadMoreButtonWrapper,
  NotFoundWrapper,
  SkeletonItemsWrapper,
  ListingItemCardWrapper,
  HotelSearchBarWrapper,
  MetaTitle,
} from '@components/views/hotel/listing/HotelListingStyle';
import { HOTEL_SORT_DATA } from '@constants/constants';
import { FlexibleDiv, Row } from '@components/formStyles';
import { useBreakpoint } from '@contexts/breakpoints';
import { ListingMetaViewModel } from '@components/views/types';
import ScrollToTop from '@shared/ScrollToTop';
import STATIC_ROUTES from '@data/staticRoutes.json';
import HotelSearchBar from '@domain/hotel/searchBar';
import { Container } from '@shared';
import ListingCard from '@components/ui/cards/ListingCard';
import findDiffDates from '@utils/findDiffDates';
import { MapPin2Line } from '@assets/icons';
import { useKKTCInfoModal } from '@hooks/useKKTCInfoModal';
import InfoModal from '@components/ui/modals/InfoModal';
import { useSearchParamsToQuery } from '@hooks/useSearchParamsToQuery';
import { useAppRouter } from '@contexts/router';
import SortingTabs from '@shared/SortingTabs';
import { useSearchURL } from '@api/data-loaders/resource';
import { HotelListSchemaOrg } from '@utils/schemaorg/HotelListSchemaOrg';

import { useListingFetching } from './hooks/useListingFetching';
import HotelListMobileHeader from './components/HotelListMobileHeader';
import { formatHotelPrices, formatHotelSliderImage } from '../utils';
import ListingFilters from './components/Filters/ListingFilters';
import { updateRoomParamInUrl } from './utils/normalizeUrl';
import Banner from './components/Banner/Banner';
import { useListingBanner } from './hooks/useListingBanner';

const ResponsiveListingFilter = dynamic(
  () =>
    import(
      '@components/views/hotel/listing/components/ResponsiveFilterAndSort/ResponsiveListingFilterAndSort'
    ),
  { ssr: false }
);

const HotelListing: FC = () => {
  const { xl } = useBreakpoint();
  const { palette } = useTheme();
  const [adultCount, setAdultCount] = useState<number>(0);
  const [childCount, setChildCount] = useState<number>(0);

  const { setSort, sort, setFilters } = useFiltersContext();

  const urlData = useSearchURL();
  const query = useSearchParamsToQuery();
  const params = useParams();

  const { push } = useAppRouter();

  const {
    isError,
    hotels,
    isFetching,
    fetchNextPage,
    filters: hotelFilters,
    meta = {} as ListingMetaViewModel,
    isFetchingNextPage,
    hasNextPage,
  } = useListingFetching({
    setAdultCount,
    setChildCount,
    setFilters,
  });

  // Fetch banner data separately
  const { banner } = useListingBanner();

  const shouldShowKKTCModal = useMemo(() => {
    return (
      hotels?.some((pages) => pages?.some((hotel) => hotel?.cyprus)) || false
    );
  }, [hotels]);
  const { showModal: showKKTCInfoModal, handleClose: handleDismiss } =
    useKKTCInfoModal(shouldShowKKTCModal || false);

  const handleTabClick = (key: string): void => {
    const item = HOTEL_SORT_DATA.find((item) => item.id === key);
    if (item) {
      setSort(item);

      // eslint-disable-next-line @typescript-eslint/no-unused-vars

      push({
        pathname: `${STATIC_ROUTES.hotelPathPrefix}/${params?.slug}`,
        query: { ...query, sort: item.id },
      });
    }
  };

  // =============================================
  // Check if the current hotels are expedia hotels
  // =============================================
  const isExpediaHotels = useMemo(() => {
    const allHotels: HotelViewModel[] = [];
    if (!hotels || hotels.length === 0) return false;
    hotels.forEach((hotel) => hotel && allHotels.push(...hotel));
    return Boolean(allHotels?.every((hotel) => hotel.expedia === true));
  }, [hotels]);

  return (
    <>
      {xl || xl === undefined ? (
        <HotelSearchBarWrapper>
          <Container>
            <HotelSearchBar isHomePage={false} />
          </Container>
        </HotelSearchBarWrapper>
      ) : (
        <HotelListMobileHeader />
      )}
      <ItemsRow>
        {(xl || xl === undefined) && hotelFilters ? (
          <ListingFilters
            isExpediaHotels={isExpediaHotels}
            hotelFilters={hotelFilters}
            isHotelsLoading={isFetching}
            hotelsCount={meta?.totalCount ?? 0}
            listingTitle={`${urlData?.text} ${
              meta?.totalCount ? `(${meta.totalCount})` : ''
            }`}
          />
        ) : null}
        <ItemsRowWrapper>
          {!isError && (xl || xl === undefined) ? (
            <FlexibleDiv top={24} bottom={24}>
              <Row alignItems='center' justifyContent='space-between'>
                {urlData?.text && meta?.totalCount ? (
                  <Row>
                    <MetaTitle bold>{urlData?.text}:</MetaTitle>
                    <MetaTitle style={{ marginLeft: 3 }}>
                      {meta.totalCount} tesis bulundu
                    </MetaTitle>
                  </Row>
                ) : (
                  <div />
                )}
                <SortingTabs
                  activeKey={sort.id}
                  data={HOTEL_SORT_DATA}
                  handleTabClick={handleTabClick}
                />
              </Row>
            </FlexibleDiv>
          ) : !xl && !isFetching && hotelFilters ? (
            <ResponsiveListingFilter
              hotelFilters={hotelFilters}
              filters={hotelFilters}
              isLoading={isFetching}
              isExpedia={isExpediaHotels}
            />
          ) : null}

          {/* Banner Section */}
          {banner && banner.items && banner.items.length > 0 && xl && (
            <Banner items={banner.items} pathPrefix='hotel' />
          )}

          {!isFetching && isError ? (
            <NotFoundWrapper>
              <NotFound
                iconSize={124}
                label='Aradığınız kriterlerde otel bulunamadı, lütfen tekrar deneyin'
              />
            </NotFoundWrapper>
          ) : null}

          {!isError && hotels && hotels?.length > 0 ? (
            <>
              <ListingItemContainer>
                {!xl ? (
                  <Row>
                    <MetaTitle bold>{urlData?.text}:</MetaTitle>
                    <MetaTitle style={{ marginLeft: 3 }}>
                      {meta.totalCount} tesis bulundu
                    </MetaTitle>
                  </Row>
                ) : null}
                {hotels.map((pages: HotelViewModel[]) => {
                  if (
                    !pages ||
                    !Array.isArray(pages) ||
                    (Array.isArray(pages) && pages.length === 0)
                  ) {
                    return null;
                  }
                  return pages.map((item, index) => {
                    return (
                      <ListingItemCardWrapper key={item.id}>
                        <ListingCard
                          title={item.name}
                          subtitles={[
                            ...(item?.location
                              ? [
                                  {
                                    text: item?.location || '',
                                    icon: MapPin2Line,
                                  },
                                ]
                              : []),
                            ...(item.boardType
                              ? [{ text: item?.boardType || '', dark: true }]
                              : []),
                          ]}
                          prices={formatHotelPrices(item?.pricing)}
                          images={formatHotelSliderImage(item.images)}
                          highlights={item.highlights
                            ?.slice(0, 4)
                            ?.map((h) => h.value)}
                          person={{ adultCount, childCount }}
                          nightsCount={
                            query?.in && query?.out
                              ? findDiffDates(
                                  String(query.in),
                                  String(query.out)
                                )
                              : undefined
                          }
                          url={updateRoomParamInUrl(item?.url ?? '')}
                          reviews={item.reviews}
                          refundable={item.refundable}
                          expedia={item.expedia}
                          events={item.events}
                          ratings={item.ratings}
                          category={item?.category}
                          priorityImage={index < 2} // First two card images get priority loading to improve LCP
                        />
                      </ListingItemCardWrapper>
                    );
                  });
                })}
              </ListingItemContainer>
            </>
          ) : null}

          {isFetching || isFetchingNextPage ? (
            <SkeletonItemsWrapper>
              {Array(9)
                .fill(0)
                .map((_, index) => (
                  <HotelCardSkeleton key={index} />
                ))}
            </SkeletonItemsWrapper>
          ) : null}
          <></>
          {!isError && hotels && hotels.length && hasNextPage ? (
            <LoadMoreButtonWrapper>
              <LoadMoreButton onClick={() => fetchNextPage()}>
                {!isFetching ? (
                  'Daha Fazla Göster'
                ) : (
                  <SyncLoader
                    color={palette.dark222}
                    loading={isFetching}
                    size={10}
                  />
                )}
              </LoadMoreButton>
            </LoadMoreButtonWrapper>
          ) : null}
        </ItemsRowWrapper>
      </ItemsRow>

      <ScrollToTop />
      {showKKTCInfoModal && (
        <InfoModal
          title={
            <h2 style={{ fontWeight: 'bold' }}>
              KKTC'YE YAPILACAK SEYAHATLER HAKKINDA ÖNEMLİ BİLGİLENDİRME
            </h2>
          }
          content={
            <div>
              <p>
                KKTC seyahatlerinde yeni tip çipli TC kimliklerinin kullanılması
                gerekmektedir. TC vatandaşlarının eski tip nüfus cüzdanları ile
                Türkiye’den çıkış işlemlerine izin verilmemektedir.
              </p>
              <p>
                0-17 yaş arası çocuklar için de <strong>fotoğraflı</strong>{' '}
                çipli kimlik kullanılması zorunludur. Kimliğinde fotoğrafı
                bulunmayan çocuk yolcular uçuşa kabul edilmemektedir. Uygulama
                yeni doğan bebekleri de kapsamaktadır.
              </p>
            </div>
          }
          onCancel={handleDismiss}
        />
      )}
      {hotels?.length ? (
        <HotelListSchemaOrg
          name={urlData?.text || meta?.title || ''}
          hotels={hotels}
        />
      ) : null}
    </>
  );
};

export default HotelListing;
